import { TICK_MS } from './time';

/**
 * THE STATE — everything the simulation carries forward.
 *
 * Every accumulated quantity here is an INTEGER. Not a convention: a design
 * decision that removes an entire class of bug. Floating-point accumulation
 * makes a live run and a settled run differ in the last bits, which forces a
 * tolerance into the offline-invariant test, and a test with a tolerance is a
 * test that cannot fail on a small real regression. Weight is stored in grams,
 * not kilograms, for exactly this reason.
 *
 * NOTE WHAT IS NOT HERE: no cosmetic, no entitlement, no purchase, no
 * multiplier, no bonus rate. Cosmetics live in the save envelope beside this
 * state (see save.ts) and never inside it, so the rate engine in step.ts cannot
 * read what a player owns even by accident. See entitlements.ts.
 */
export interface SimState {
  /** uint32. With the tick index, it addresses every random draw in the run. */
  readonly seed: number;
  /** Absolute wall-clock ms at which tick 0 began. Progress is measured from this. */
  readonly epochMs: number;
  /** Integer tick cursor: the index of the NEXT tick to run. */
  readonly tick: number;

  readonly xp: number;
  readonly coins: number;
  /** Fish landed, by species id. May contain ids this build does not know; see save.ts. */
  readonly catches: Readonly<Record<string, number>>;
  readonly junk: number;

  /**
   * PER-SPECIES MASTERY — the cost of the player's one verb, by species id.
   *
   * ONE POINT IS ONE TICK OF A CAST THAT LANDED THAT SPECIES, so a total divided
   * by 3,600 is hours. CONTRACT.md section 6 property 3 names this as the thing
   * targeting must trade against: "hours spent targeting A are hours not spent
   * building mastery in B". Before this field the choice was free, which is what
   * property 3 exists to forbid. See mastery.ts for the whole derivation,
   * including why it cannot be reconstructed from `catches`.
   *
   * NO TEMPO BRANCHES ON IT, AND THAT IS FORCED BY PROPERTY 2: a targeted and an
   * untargeted run must land the same catch budget with zero tolerance, and any
   * throughput effect of mastery would accumulate only under a target and break
   * the equality on the first tick it applied. So the cast length, the outcome
   * roll, the xp and the coins cannot see it and there is no expression in which
   * they could.
   *
   * THE COMPOSITION DOES BRANCH ON IT, once, and this docstring used to deny it.
   * CONTRACT.md section 1.5l(a) rules that mastery deepens the species it is
   * spent on, so `stepTick` reads this field to decide which FORMS of the quarry
   * are available and a form scales that cast's weight. See `variants` below and
   * src/sim/variants.ts. The line the properties draw is between WHAT you land
   * and HOW FAST, not between reading a field and not reading it.
   *
   * Same shape and same rules as `catches`: keys sorted, unknown ids KEPT rather
   * than dropped on load, earned rather than bounded, and never reduced by a
   * loader except at MAX_EARNED with a note.
   */
  readonly mastery: Readonly<Record<string, number>>;

  /**
   * PER-SPECIES PERSONAL BEST, IN GRAMS. The heaviest single fish of that
   * species this save has ever landed.
   *
   * IT EXISTED AS A MEASUREMENT BEFORE IT EXISTED AS A FIELD, and that is the
   * defect this closes. CONTRACT.md section 1.5l(a) rules that mastery deepens
   * the species it is spent on and that "the ledger records a personal best";
   * section 6 property 3's dominance probe then began asserting on an AGGREGATE
   * axis set whose third axis is `recordG` — a per-species best weight. The
   * probe computed that axis inside itself, from the same species table the
   * engine uses, and passed. Nothing in `SimState` or `save.ts` carried it. So a
   * green fence stood on a quantity the game did not persist: it measured an
   * intention, and a reload would have deleted whatever it was measuring.
   *
   * IT IS EARNED AND IT IS THE FIRST FIELD IN THIS STATE THAT RISES BY MAXIMUM
   * RATHER THAN BY SUM, which is the whole reason it needs its own fence. Every
   * other earned quantity here accumulates through `addCapped`, so "never nerf"
   * and "goes up" are the same sentence about addition. A record is a maximum:
   * `stepTick` writes `max(previous, weight)`, and the failure mode that belongs
   * to a maximum is not an underflow but a LATER, SMALLER value overwriting an
   * earlier, larger one. A shrinking catch, a reload that reconstructs from the
   * wrong side, and an offline settle that starts from an empty map are three
   * different ways to write that same defect, and record.test.ts fires on all
   * three. See record.ts for the derivation.
   *
   * Same shape and same rules as `catches` and `mastery`: keys sorted, unknown
   * ids KEPT rather than dropped on load, bounded by MAX_EARNED and by nothing
   * narrower, and never reduced by a loader without a note.
   */
  readonly records: Readonly<Record<string, number>>;

  /**
   * FORMS LANDED, BY `<species id>:<variant id>` — the reward half of the ruling
   * mastery pays for.
   *
   * CONTRACT.md section 1.5l(a): "Mastering mackerel changes mackerel: larger
   * and rarer variants begin appearing... and FORMS EXIST THAT A LOW-MASTERY
   * PLAYER NEVER SEES." `records` made the ledger half real in round 12; this is
   * the other half, and without it the only trace an Elder Mackerel leaves is a
   * personal best that happens to sit above the species' base band. That is a
   * number a player can read and not a thing the game can name.
   *
   * IT IS NOT RECONSTRUCTIBLE, which is the same argument `mastery` and
   * `records` each had to make to earn a field. A form is decided by
   * `variantFor(mastery-at-that-tick, roll)`, so recovering the tally from a
   * finished save would mean replaying every tick of the run with the mastery
   * total it had at the time. `catches` says how many; `weightG` says how much;
   * `records` says the largest single one. None of them says how many Elder
   * Mackerel this save has ever landed, and that is the sentence the ledger
   * wants to print.
   *
   * IT IS COUNTED, NOT FLAGGED. A boolean "seen" would be smaller and would lose
   * the difference between the first Elder and the fortieth, which at 1 landing
   * in 1,000 is the difference between an afternoon and a season.
   *
   * Same shape and same rules as `catches`, `mastery` and `records`: keys
   * sorted, unknown keys KEPT rather than dropped on load, bounded by MAX_EARNED
   * and by nothing narrower, and never reduced by a loader without a note. The
   * key differs — it names a species AND a form — which is why `save.ts` takes
   * the known-key predicate as an argument rather than assuming `isKnownSpecies`.
   */
  readonly variants: Readonly<Record<string, number>>;
  readonly specials: number;
  /** Total landed weight in grams. Integer. */
  readonly weightG: number;

  /** Whole ticks elapsed in the cast now in progress. */
  readonly castProgress: number;
  /** Species id the player has chosen, or '' for "the best I have unlocked". */
  readonly targetSpecies: string;

  /**
   * Skill, 0-100. DESIGN LAW: skill buys SPEED and never gates the catch. At
   * skill 0 the game plays itself and lands everything; at skill 100 it lands
   * the same things sooner. step.test.ts proves both halves.
   *
   * IT IS A PERSISTED ATTRIBUTE OF THE SAVE, NEVER A LIVE INPUT. That is now an
   * OWNER RULING (2026-08-31, PRESENCE CHANGES NOTHING) and not a builder's
   * reading: the contract used to ask for two things that cannot both be true of
   * a live input — "being away pays exactly what being present pays" and "an
   * always-inputs controller catches faster than a never-inputs one" — and the
   * ruling resolved it by making the offline invariant win outright. The game
   * plays identically whether it is watched or not.
   *
   * The probe direction inverted with it: a never-inputs controller and an
   * always-inputs controller over the same seed must produce IDENTICAL states,
   * which step.test.ts asserts to the byte across three seeds and three skill
   * levels. A skilled player is skilled while away, the settle runs at their
   * rate, and the invariant stays exact.
   *
   * The residue is stated rather than absorbed: skill is a rate lever that lives
   * in the save, so it is the one field a monetisation layer would have to reach
   * to sell speed. entitlements.ts is why it cannot — the rate engine's import
   * closure has no route to it, and the entitlement type has nowhere to put a
   * number.
   */
  readonly skill: number;

  /**
   * Ticks that fell beyond the hold and were SKIPPED, never paid at a reduced
   * rate. Reported to the player so absence is honest, and counted here so
   * hold.test.ts can prove no second code path ever pays them out later.
   */
  readonly skippedTicks: number;
}

/**
 * KEY NAMES THAT IDENTIFY A PURCHASE RECORD WHEREVER ONE APPEARS.
 *
 * The mirror image of RATE_SHAPED_KEYS in cosmetic-keys.ts, and deliberately a
 * separate list rather than a reuse of it: that one names what an ENTITLEMENT
 * must never carry, this one names what the SIMULATION STATE must never carry.
 * Wider than a careless commit would actually write, because a near-miss name
 * is how this rule dies — `unlocks` reads far more innocent in a diff than
 * `wardrobe` and is exactly as fatal.
 */
type OwnershipShapedKey =
  | 'wardrobe'
  | 'owned'
  | 'equipped'
  | 'entitlement'
  | 'entitlements'
  | 'cosmetic'
  | 'cosmetics'
  | 'grants'
  | 'granted'
  | 'catalogue'
  | 'catalog'
  | 'purchase'
  | 'purchases'
  | 'purchased'
  | 'receipt'
  | 'sku'
  | 'iap'
  | 'unlocks'
  | 'supporter';

/** Depth budget, so a recursive shape terminates instead of exhausting tsc. */
type Decrement = [never, 0, 1, 2, 3, 4, 5, 6, 7];

/**
 * TRUE ONLY WHILE `T` CARRIES NO PURCHASE RECORD AT ANY DEPTH.
 *
 * SIM-H1, and the half of that finding a type CAN answer. The round-4 firewall
 * said no module that moves a quantity may REACH the ownership module; six live
 * paid multipliers then walked past it, because counting a purchase needs no
 * import — `envelope.wardrobe.owned.length` was already in scope. This is the
 * complementary statement, made to the compiler rather than to a scanner: the
 * value the rate engine receives has no cosmetic field anywhere in its
 * transitive type, so `owned.length` cannot be WRITTEN there at all.
 *
 * WHAT IT DOES NOT PROVE, stated here because the round that shipped it argued
 * the point and lost. It constrains the TYPE, not the SCOPE. `persistence.ts`
 * holds a SimState and a Wardrobe as two locals in one function, and no type
 * relates them; `save.ts` must mint earned integers out of `unknown`, which
 * launders any brand. A multiplier respecting every type in this file still
 * pays. The guarantee is `tools/instruments/paywin.mjs` rule C — purchase
 * invariance measured on values — and this assertion is the cheap half that
 * makes the obvious mistake impossible rather than merely uncustomary.
 */
export type CosmeticFree<T, D extends 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 = 8> = D extends 0
  ? true
  : [T] extends [readonly (infer U)[]]
    ? CosmeticFree<U, Decrement[D]>
    : [T] extends [object]
      ? [Extract<keyof T, OwnershipShapedKey>] extends [never]
        ? { [K in keyof T]-?: CosmeticFree<T[K], Decrement[D]> }[keyof T] extends true
          ? true
          : false
        : false
      : true;

/**
 * COMPILE-TIME PROOF. Add `wardrobe`, `owned`, `purchases` or any other
 * ownership-shaped key to SimState — or to anything SimState transitively
 * contains — and `true` stops being assignable here. `npx tsc --noEmit` fails
 * before a single test runs, which is the same mechanism
 * ENTITLEMENT_IS_ACCESS_ONLY uses on the other side of the wall.
 *
 * Its non-vacuity control lives in save.ts, on SaveEnvelope, which DOES carry a
 * wardrobe: an assertion that cannot come out false is an assertion about
 * nothing.
 */
export const SIM_STATE_IS_COSMETIC_FREE: CosmeticFree<SimState> = true;

/**
 * Field bounds. Applied in two places that must agree: the single normaliser in
 * save.ts, which is what a loaded save passes through, and addCapped() in
 * step.ts, which is what a running simulation accumulates against.
 *
 * A ceiling is not a nerf. Nothing already earned decreases when a value meets
 * one; it stops rising. They exist so that no accumulator can leave safe-integer
 * range, because past 2^53 integer addition silently stops being exact and the
 * offline invariant would start failing for a reason that has nothing to do
 * with offline play.
 */
export const MAX_SEED = 0xffffffff;
export const MAX_SKILL = 100;
/** RuneScape's own experience ceiling, reused because the curve is. */
export const MAX_XP = 200_000_000;
export const MAX_COINS = 1_000_000_000_000_000;
export const MAX_COUNT = 1_000_000_000_000;
export const MAX_WEIGHT_G = 1_000_000_000_000_000;

/**
 * THE ONLY BOUND A LOADER MAY PUT ON AN EARNED QUANTITY.
 *
 * SIM-D4, and the distinction that finding turned on. Everything above is a
 * GAMEPLAY ceiling: it stops a value rising inside THIS build, and addCapped
 * honours it. None of them is a bound a loader may impose on a value ANOTHER
 * build already awarded. Round 2 used them as one, and MEASURED
 * (`node src/sim/measure/run.mjs nerf-scalars`) it reduced six earned scalars on
 * load with an EMPTY notes array — xp 600,000,000 -> 200,000,000, four hundred
 * million xp deleted at load, made permanent by the next autosave — while
 * reporting the identical event on the seventh field, the catches map, because
 * that is the one field a round-1 critic happened to catch.
 *
 * So a loaded earned quantity is bounded by this and by nothing else. It exists
 * for arithmetic safety alone: 2^52 leaves the whole upper half of the
 * safe-integer range free, which is more headroom than every tick the hold can
 * ever pay could consume, so addition stays exact and the offline invariant
 * keeps failing only for reasons that are actually about offline play.
 */
export const MAX_EARNED = 2 ** 52;

/**
 * EVERY FIELD OF SimState, CLASSIFIED BY WHAT A LOADER IS ALLOWED TO DO TO IT.
 *
 * This registry exists so that never-nerf is enforced as a CLASS. Round 2's
 * enforcement was four tests, all on the catches map, and six fields of exactly
 * the same kind went unguarded — the tell being asymmetric reporting, one note
 * on one field and silence on the rest. save.test.ts now asserts that this
 * registry's union is EXACTLY the key set of a real state, so the NEXT field
 * cannot be added to SimState without someone deciding, in this file, which of
 * these three things it is. (The sentence used to say "a fourteenth field" and
 * the state has had fifteen and now sixteen; a rule that counts is a rule that
 * goes stale, so it counts nothing.)
 */
export const FIELD_CLASS = {
  /**
   * ACCUMULATED PROGRESS. A loader may never reduce one. The only clamp allowed
   * is MAX_EARNED, and any reduction it forces is reported in the load notes.
   */
  /**
   * `mastery` IS EARNED AND THE CLASSIFICATION IS THE WHOLE DECISION, so it is
   * argued here rather than assumed. The three classes ask different questions:
   * `cursor` is a clock position that repairCursor may reset to zero and lose
   * nothing; `bounded` is a value with no meaning outside its range, so clamping
   * it takes nothing away. Mastery is neither. It is HOURS A PLAYER SPENT, it
   * accumulates without limit inside the range, and a loader that reduced one
   * would be deleting time somebody lived through — which is the never-nerf rule
   * in its plainest form. It is bounded by MAX_EARNED like every other earned
   * quantity and by nothing narrower, and any reduction is reported.
   *
   * It is the second MAP in this class, after `catches`. never-nerf.test.ts
   * derives which earned fields are maps from a real state rather than naming
   * them, so a third map is covered on the day it is added.
   *
   * `records` IS THAT THIRD MAP, AND IT IS EARNED FOR A REASON THE OTHER TWO DO
   * NOT SHARE. `catches` and `mastery` are sums; a loader that reduced one is
   * deleting fish or hours. A record is a MAXIMUM, so a loader that reduced one
   * is deleting a specific afternoon: the single biggest fish this save ever
   * landed, which is the one number a player would notice going backwards
   * without any tooling at all. It is bounded by MAX_EARNED like every other
   * earned quantity and by nothing narrower, and any reduction is reported.
   *
   * The derivation is deliberately NOT a third rule: never-nerf.test.ts reads
   * the map/scalar split off a real state's values, so this field arrived in the
   * right half of that file without being named there.
   */
  /**
   * `variants` IS THE FOURTH MAP AND IT IS EARNED FOR THE SAME REASON `catches`
   * IS: it is a count of things a player landed. A loader that reduced one is
   * deleting fish — and specifically the rarest fish the save has, the ones that
   * took sixty hours in one species to become possible at all. It is bounded by
   * MAX_EARNED like every other earned quantity and by nothing narrower, and any
   * reduction is reported.
   *
   * Nothing else in never-nerf.test.ts had to be told: that file reads the
   * map/scalar split off a real state's VALUES, so this field sorted itself into
   * the map half on the day it landed. One line moved there, and it is the line
   * that says out loud how many maps the derivation currently sees.
   */
  earned: ['catches', 'coins', 'junk', 'mastery', 'records', 'skippedTicks', 'specials', 'variants', 'weightG', 'xp'],
  /**
   * CLOCK POSITION. Bounded by CURSOR_CEILING (see time.ts). Not earned:
   * repairCursor may reset one to zero and lose nothing, which is why the
   * never-nerf rule does not reach here and why it must not be extended to.
   */
  cursor: ['castProgress', 'epochMs', 'tick'],
  /**
   * STRUCTURALLY BOUNDED — the value has no meaning outside its range, so a
   * loader clamping it is not taking anything away.
   *
   * `skill` IS THE RESIDUE, AND IT IS STATED RATHER THAN ABSORBED. It is a rate
   * lever the player earns, not a structural constant, and this build clamps a
   * loaded skill to MAX_SKILL. If a later build ever raises the skill cap, a
   * roll-back through this build will round a 150-skill save down to 100 and
   * that IS a nerf. It is bounded here anyway because castTicksFor clamps skill
   * to 100 at the point of use regardless, so carrying 150 through the save
   * would carry a number nothing in this build can read. The cost is a required
   * migration on the day MAX_SKILL moves; it is logged as an open item in
   * docs/LANES/sim-core.md rather than discovered then.
   */
  bounded: ['seed', 'skill', 'targetSpecies'],
} as const;

/** The seed a save falls back to when its own is unreadable. Arbitrary, fixed, non-zero. */
export const FALLBACK_SEED = 0x5eed_face >>> 0;

/**
 * A fresh run.
 *
 * `nowMs` is handed in by the caller. Nothing under src/sim/ ever calls
 * Date.now itself — that is the purity law in time.ts, and it is what lets a
 * test place a run anywhere on the timeline it likes.
 */
export function createState(seed: number, nowMs: number): SimState {
  const s = Number.isFinite(seed) ? Math.trunc(seed) >>> 0 : FALLBACK_SEED;
  const anchor = Number.isFinite(nowMs) ? Math.floor(nowMs / TICK_MS) * TICK_MS : 0;
  // KEYS IN SORTED ORDER, and every other constructor of a SimState or a save
  // envelope in this lane does the same. SIM-D2: the loader rebuilds objects and
  // the simulator spreads them, so (load . save) was the identity on every VALUE
  // and not on the OBJECT — an honest 10-minute run came back deep-unequal with
  // its catch keys permuted. Sorted construction everywhere is what makes the
  // round trip a fixed point under RAW JSON.stringify rather than only under a
  // comparator that sorts the difference away. roundtrip.test.ts asserts it.
  return {
    castProgress: 0,
    catches: {},
    coins: 0,
    epochMs: anchor,
    junk: 0,
    mastery: {},
    records: {},
    seed: s,
    skill: 0,
    skippedTicks: 0,
    specials: 0,
    targetSpecies: '',
    tick: 0,
    variants: {},
    weightG: 0,
    xp: 0,
  };
}
